import {AfterViewChecked, ChangeDetectorRef, Component, Input, OnChanges, OnDestroy} from '@angular/core';
import {UntypedFormGroup, Validators} from '@angular/forms';
import {Subscription} from 'rxjs';
import {Eviction, IntakeFormSection, IntakeOption} from '@ee/common/models';
import Big from 'big.js';
import {IntakeFormFields, RuleTargetAction, RuleTargetType} from '@ee/common/enums';
import {ConditionEvaluatorService} from '@ee/common/services';
import {CURRENCY} from '@ee/common/constants';

@Component({
  selector: 'ee-claims-section',
  template: `
    @if (!hideSection) {
      <div class="section-wrapper" [formGroup]="form">
        @if (options[intakeFields.CLAIMS]?.type === 'simple') {
          <div class="flex flex-col justify-start items-stretch">
            <div class="flex flex-row justify-center items-center mb-4">
              <mat-form-field>
                <mat-label>Total Due</mat-label>
                <span matPrefix>$ &nbsp;</span>
                <input
                  matInput
                  type="number"
                  formControlName="tenant_total_owed"
                  required
                  (click)="$any($event.target)?.select()"
                  autocomplete="off" />
                @if (form?.get('tenant_total_owed').errors) {
                  <mat-error>Total must be greater than 0.</mat-error>
                }
                <mat-hint>Required. {{ claimsSection?.localization?.['tenant_total_owed_hint'] }}</mat-hint>
              </mat-form-field>
            </div>
            @if (claimsSection?.fields?.length && claimsFields) {
              <div class="flex flex-row flex-wrap">
                @for (f of claimsSection?.fields; track f; let i = $index) {
                  <ee-field
                    class="mr-4 mb-4"
                    [ngClass]="{
                      'margin-bottom-sm': i < claimsSection.fields.length - 1,
                      'md:mb-4': i < claimsSection.fields.length - 1,
                      'double-column': f.columns === 2,
                      'triple-column': f.columns === 3,
                      'quad-column': f.columns === 4
                    }"
                    [hideFromIntake]="f.hide_from_intake"
                    [hidden]="f.hide_from_intake && !overrideFieldVisibility"
                    [control]="$any(claimsFields.controls[f.field])"
                    [field]="f"></ee-field>
                }
              </div>
            }
          </div>
        } @else {
          @if (options[intakeFields.PAST_DUE_RENT_HISTORY]?.show) {
            <ee-past-due-rent-history [form]="form" (valueChanged)="updateTotal($event)"></ee-past-due-rent-history>
          }
          <div class="in-row-grid">
            @if (
              (options[intakeFields.PAST_DUE_RENT].show || overrideFieldVisibility) &&
              !options[intakeFields.PAST_DUE_RENT_HISTORY]?.show
            ) {
              <mat-form-field
                class="flex-1 md:flex-none"
                [ngClass]="{ 'hidden-from-intake': !!options[intakeFields.PAST_DUE_RENT]?.hide_from_intake }">
                <mat-label>Rent Owed</mat-label>
                <span matPrefix>$ &nbsp;</span>
                <input
                  matInput
                  type="number"
                  formControlName="past_due_rent"
                  [required]="options[intakeFields.PAST_DUE_RENT].required" />
                @if (form?.get('past_due_rent').hasError('required')) {
                  <mat-error>Please enter a value.</mat-error>
                }
                @if (form?.get('past_due_rent').hasError('pattern')) {
                  <mat-error>Invalid currency format.</mat-error>
                }
                <mat-hint class="whitespace-nowrap">
                  {{ options[intakeFields.PAST_DUE_RENT].required ? 'Required' : 'Optional' }}
                  @if (claimsSection?.localization?.['past_due_rent_hint']) {
                    <span class="field-instructions-wrapper">
                      . Hover for
                      <span
                        class="field-instructions"
                        [tp]="claimsSection?.localization?.['past_due_rent_hint']"
                        tpPlacement="bottom">
                        more info
                      </span>
                      .
                    </span>
                  }
                </mat-hint>
              </mat-form-field>
            }
            @if (showField(intakeFields.LATE_FEES, options[intakeFields.LATE_FEES])) {
              <mat-form-field
                class="flex-1 md:flex-none"
                [ngClass]="{ 'hidden-from-intake': !!options[intakeFields.LATE_FEES]?.hide_from_intake }">
                <mat-label>Late Fee</mat-label>
                <span matPrefix>$ &nbsp;</span>
                <input
                  matInput
                  type="number"
                  formControlName="late_fees"
                  [required]="options[intakeFields.LATE_FEES].required" />
                @if (form?.get('late_fees').hasError('required')) {
                  <mat-error>Please enter a value.</mat-error>
                }
                @if (form?.get('late_fees').hasError('pattern')) {
                  <mat-error>Invalid currency format.</mat-error>
                }
                <mat-hint class="whitespace-nowrap">
                  {{ options[intakeFields.LATE_FEES].required ? 'Required' : 'Optional' }}
                  @if (claimsSection?.localization?.['late_fees_hint']) {
                    <span class="field-instructions-wrapper">
                      . Hover for
                      <span class="field-instructions" [tp]="claimsSection?.localization?.['late_fees_hint']" tpPlacement="bottom">
                        more info
                      </span>
                      .
                    </span>
                  }
                </mat-hint>
              </mat-form-field>
            }
            @if (showField(intakeFields.DELINQUENCY_LENGTH, options[intakeFields.DELINQUENCY_LENGTH])) {
              <mat-form-field
                class="double-column"
                [ngClass]="{ 'hidden-from-intake': !!options[intakeFields.DELINQUENCY_LENGTH]?.hide_from_intake }">
                <mat-label>Delinquency Length</mat-label>
                <input
                  matInput
                  type="text"
                  formControlName="delinquency_length"
                  [required]="options[intakeFields.DELINQUENCY_LENGTH].required" />
                @if (form?.get('delinquency_length').hasError('required')) {
                  <mat-error>Please enter a value.</mat-error>
                }
                <mat-hint class="whitespace-nowrap">
                  {{ options[intakeFields.DELINQUENCY_LENGTH].required ? 'Required' : 'Optional' }}.&nbsp; Ex. Jan 1,
                  {{ year }} - Mar 1, {{ year }}.
                  @if (claimsSection?.localization?.['delinquency_length_hint']) {
                    <span class="field-instructions-wrapper">
                      . Hover for
                      <span
                        class="field-instructions"
                        [tp]="claimsSection?.localization?.['delinquency_length_hint']"
                        tpPlacement="bottom">
                        more info
                      </span>
                      .
                    </span>
                  }
                </mat-hint>
              </mat-form-field>
            }
          </div>
          @if (showingAtLeastOne(intakeFields.DAMAGES, intakeFields.DAMAGES_DESCRIPTION)) {
            <div class="in-row-grid">
              @if (showField(intakeFields.DAMAGES, options[intakeFields.DAMAGES])) {
                <mat-form-field
                  class="flex-1 md:flex-none"
                  [ngClass]="{ 'hidden-from-intake': !!options[intakeFields.DAMAGES]?.hide_from_intake }">
                  <mat-label>Damages</mat-label>
                  <span matPrefix>$ &nbsp;</span>
                  <input
                    matInput
                    type="number"
                    formControlName="damages"
                    [required]="options[intakeFields.DAMAGES].required" />
                  @if (form.get('damages').hasError('required')) {
                    <mat-error>Please enter a value.</mat-error>
                  }
                  @if (form.get('damages').hasError('pattern')) {
                    <mat-error>Invalid currency format.</mat-error>
                  }
                  <mat-hint class="whitespace-nowrap">
                    {{ options[intakeFields.DAMAGES].required ? 'Required' : 'Optional' }}
                    @if (claimsSection?.localization?.['damages_hint']) {
                      <span class="field-instructions-wrapper">
                        . Hover for
                        <span class="field-instructions" [tp]="claimsSection?.localization?.['damages_hint']" tpPlacement="bottom">
                          more info
                        </span>
                        .
                      </span>
                    }
                  </mat-hint>
                </mat-form-field>
              }
              @if (showField(intakeFields.DAMAGES_DESCRIPTION, options[intakeFields.DAMAGES_DESCRIPTION])) {
                <mat-form-field
                  class="triple-column"
                  [ngClass]="{ 'hidden-from-intake': !!options[intakeFields.DAMAGES_DESCRIPTION]?.hide_from_intake }">
                  <mat-label>Damage Description</mat-label>
                  <input
                    matInput
                    type="text"
                    formControlName="damages_description"
                    [required]="options[intakeFields.DAMAGES_DESCRIPTION].required" />
                  @if (form?.get('damages_description').hasError('required')) {
                    <mat-error>Please enter a description.</mat-error>
                  }
                  <mat-hint class="whitespace-nowrap">
                    {{ options[intakeFields.DAMAGES_DESCRIPTION].required ? 'Required' : 'Optional' }}
                    @if (claimsSection?.localization?.['damages_description_hint']) {
                      <span class="field-instructions-wrapper">
                        . Hover for
                        <span
                          class="field-instructions"
                          [tp]="claimsSection?.localization?.['damages_description_hint']"
                          tpPlacement="bottom">
                          more info
                        </span>
                        .
                      </span>
                    }
                  </mat-hint>
                </mat-form-field>
              }
            </div>
          }
          @if (showingAtLeastOne(intakeFields.INTEREST_RATE, intakeFields.INTEREST_START_DATE)) {
            <div class="in-row-grid">
              @if (showField(intakeFields.INTEREST_RATE, options[intakeFields.INTEREST_RATE])) {
                <mat-form-field
                  [ngClass]="{ 'hidden-from-intake': !!options[intakeFields.INTEREST_RATE]?.hide_from_intake }">
                  <mat-label>Interest Rate</mat-label>
                  <input
                    matInput
                    type="number"
                    formControlName="interest_rate"
                    [required]="options[intakeFields.INTEREST_RATE].required" />
                  <span matSuffix>%</span>
                  @if (form?.get('interest_rate').hasError('required')) {
                    <mat-error>Please enter a value.</mat-error>
                  }
                  <mat-hint class="whitespace-nowrap">
                    {{ options[intakeFields.INTEREST_RATE].required ? 'Required' : 'Optional' }}
                    @if (claimsSection?.localization?.['interest_rate_hint']) {
                      <span class="field-instructions-wrapper">
                        . Hover for
                        <span
                          class="field-instructions"
                          [tp]="claimsSection?.localization?.['interest_rate_hint']"
                          tpPlacement="bottom">
                          more info
                        </span>
                        .
                      </span>
                    }
                  </mat-hint>
                </mat-form-field>
              }
              @if (showField(intakeFields.INTEREST_START_DATE, options[intakeFields.INTEREST_START_DATE])) {
                <mat-form-field
                  [ngClass]="{ 'hidden-from-intake': !!options[intakeFields.INTEREST_START_DATE]?.hide_from_intake }">
                  <mat-label>Interest Start Date</mat-label>
                  <input
                    matInput
                    [matDatepicker]="dpInterest"
                    formControlName="interest_start_date"
                    [required]="options[intakeFields.INTEREST_START_DATE].required" />
                  @if (form?.get('interest_start_date').hasError('required')) {
                    <mat-error>Please enter a value.</mat-error>
                  }
                  <mat-hint class="whitespace-nowrap">
                    {{ options[intakeFields.INTEREST_START_DATE].required ? 'Required' : 'Optional' }}
                    @if (claimsSection?.localization?.['interest_start_date_hint']) {
                      <span class="field-instructions-wrapper">
                        . Hover for
                        <span
                          class="field-instructions"
                          [tp]="claimsSection?.localization?.['interest_start_date_hint']"
                          tpPlacement="bottom">
                          more info
                        </span>
                        .
                      </span>
                    }
                  </mat-hint>
                  <mat-datepicker-toggle matSuffix [for]="dpInterest"></mat-datepicker-toggle>
                  <mat-datepicker #dpInterest></mat-datepicker>
                </mat-form-field>
              }
            </div>
          }
          @if (
            showingAtLeastOne(
              intakeFields.ATTORNEY_FEE,
              intakeFields.CIVIL_RECOVERY_FEES,
              intakeFields.COSTS,
              intakeFields.PARTIAL_PAYMENTS
            )
          ) {
            <div class="in-row-grid">
              @if (showField(intakeFields.ATTORNEY_FEE, options[intakeFields.ATTORNEY_FEE])) {
                <mat-form-field
                  [ngClass]="{ 'hidden-from-intake': !!options[intakeFields.ATTORNEY_FEE]?.hide_from_intake }">
                  <mat-label>Attorney Fee</mat-label>
                  <span matPrefix>$ &nbsp;</span>
                  <input
                    matInput
                    type="number"
                    formControlName="attorney_fee"
                    [required]="options[intakeFields.ATTORNEY_FEE].required" />
                  @if (form?.get('attorney_fee').hasError('required')) {
                    <mat-error>Please enter a value.</mat-error>
                  }
                  @if (form?.get('attorney_fee').hasError('pattern')) {
                    <mat-error>Invalid currency format.</mat-error>
                  }
                  <mat-hint class="whitespace-nowrap">
                    {{ options[intakeFields.ATTORNEY_FEE].required ? 'Required' : 'Optional' }}
                    @if (claimsSection?.localization?.['attorney_fee_hint']) {
                      <span class="field-instructions-wrapper">
                        . Hover for
                        <span
                          class="field-instructions"
                          [tp]="claimsSection?.localization?.['attorney_fee_hint']"
                          tpPlacement="bottom">
                          more info
                        </span>
                        .
                      </span>
                    }
                  </mat-hint>
                </mat-form-field>
              }
              @if (showField(intakeFields.CIVIL_RECOVERY_FEES, options[intakeFields.CIVIL_RECOVERY_FEES])) {
                <mat-form-field
                  [ngClass]="{ 'hidden-from-intake': !!options[intakeFields.CIVIL_RECOVERY_FEES]?.hide_from_intake }">
                  <mat-label>Civil Recovery Fee</mat-label>
                  <span matPrefix>$ &nbsp;</span>
                  <input
                    matInput
                    type="number"
                    formControlName="civil_recovery_fees"
                    [required]="options[intakeFields.CIVIL_RECOVERY_FEES].required" />
                  @if (form?.get('civil_recovery_fees').hasError('required')) {
                    <mat-error>Please enter a value.</mat-error>
                  }
                  @if (form?.get('civil_recovery_fees').hasError('pattern')) {
                    <mat-error>Invalid currency format.</mat-error>
                  }
                  <mat-hint class="whitespace-nowrap">
                    {{ options[intakeFields.CIVIL_RECOVERY_FEES].required ? 'Required' : 'Optional' }}
                    @if (claimsSection?.localization?.['civil_recovery_fees_hint']) {
                      <span class="field-instructions-wrapper">
                        . Hover for
                        <span
                          class="field-instructions"
                          [tp]="claimsSection?.localization?.['civil_recovery_fees_hint']"
                          tpPlacement="bottom">
                          more info
                        </span>
                        .
                      </span>
                    }
                  </mat-hint>
                </mat-form-field>
              }
              @if (showField(intakeFields.COSTS, options[intakeFields.COSTS])) {
                <mat-form-field [ngClass]="{ 'hidden-from-intake': !!options[intakeFields.COSTS]?.hide_from_intake }">
                  <mat-label>Costs</mat-label>
                  <span matPrefix>$ &nbsp;</span>
                  <input
                    matInput
                    type="number"
                    formControlName="costs"
                    [required]="options[intakeFields.COSTS].required" />
                  @if (form?.get('costs').hasError('required')) {
                    <mat-error>Please enter a value.</mat-error>
                  }
                  @if (form?.get('costs').hasError('pattern')) {
                    <mat-error>Invalid currency format.</mat-error>
                  }
                  <mat-hint class="whitespace-nowrap">
                    {{ options[intakeFields.COSTS].required ? 'Required' : 'Optional' }}
                      @if (claimsSection?.localization?.['costs_hint']) {
                      <span class="field-instructions-wrapper">
                        . Hover for
                        <span class="field-instructions" [tp]="claimsSection?.localization?.['costs_hint']" tpPlacement="bottom">
                          more info
                        </span>
                        .
                      </span>
                    }
                  </mat-hint>
                </mat-form-field>
              }
              @if (showField(intakeFields.PARTIAL_PAYMENTS, options[intakeFields.PARTIAL_PAYMENTS])) {
                <mat-form-field
                  [ngClass]="{ 'hidden-from-intake': !!options[intakeFields.PARTIAL_PAYMENTS]?.hide_from_intake }">
                  <mat-label>Partial Payments</mat-label>
                  <span matPrefix>$ &nbsp;</span>
                  <input
                    matInput
                    type="number"
                    formControlName="partial_payments"
                    [required]="options[intakeFields.PARTIAL_PAYMENTS].required" />
                  @if (form?.get('partial_payments').hasError('required')) {
                    <mat-error>Please enter a value.</mat-error>
                  }
                  @if (form?.get('partial_payments').hasError('pattern')) {
                    <mat-error>Invalid currency format.</mat-error>
                  }
                  <mat-hint class="whitespace-nowrap">
                    {{ options[intakeFields.PARTIAL_PAYMENTS].required ? 'Required' : 'Optional' }}
                    @if (claimsSection?.localization?.['partial_payments_hint']) {
                      <span class="field-instructions-wrapper">
                        . Hover for
                        <span
                          class="field-instructions"
                          [tp]="claimsSection?.localization?.['partial_payments_hint']"
                          tpPlacement="bottom">
                          more info
                        </span>
                        .
                      </span>
                    }
                  </mat-hint>
                </mat-form-field>
              }
            </div>
          }
          @if (claimsSection?.fields?.length && claimsFields) {
            <div class="in-row-grid">
              @for (f of claimsSection?.fields; track f; let i = $index) {
                <ee-field
                  [compact]="false"
                  [ngClass]="{
                    'margin-bottom-sm': i < claimsSection.fields.length - 1,
                    'md:mb-4': i < claimsSection.fields.length - 1,
                    'double-column': f.columns === 2,
                    'triple-column': f.columns === 3,
                    'quad-column': f.columns === 4
                  }"
                  [hideFromIntake]="f.hide_from_intake"
                  [hidden]="f.hide_from_intake && !overrideFieldVisibility"
                  [control]="$any(claimsFields.controls[f.field])"
                  [field]="f"></ee-field>
              }
            </div>
          }
          <div class="in-row-grid" style="display:none !important;">
            <mat-form-field class="compact total" appearance="fill" tp="Calculated field." tpPlacement="left">
              <input matInput formControlName="tenant_total_owed" readonly />
            </mat-form-field>
          </div>
          @if (form?.get('tenant_total_owed')) {
            <div class="triple-column flex flex-col justify-end items-end">
              <div class="total">Total Amount: {{ getTotalOutstandingAmount | currency }}</div>
              @if (form?.get('tenant_total_owed').errors) {
                <mat-error>Total must be greater than 0.</mat-error>
              }
            </div>
          }
        }
      </div>
    } @else {
      <div class="flex flex-row justify-center items-center">
        <div class="mt-4 mb-4">Claims are not required.</div>
      </div>
    }
  `,
  standalone: false
})
export class ClaimsSectionComponent implements OnChanges, AfterViewChecked, OnDestroy {
  @Input() demoMode = false;

  @Input() overrideFieldVisibility = false;

  @Input() form: UntypedFormGroup;

  @Input() clearOnSwitch = false;

  @Input() options: Map<string, IntakeOption> | undefined;

  @Input() claimsSection: IntakeFormSection | undefined;

  @Input() evictionReason: string | undefined;

  @Input() eviction: Eviction | undefined;

  intakeFields = IntakeFormFields;

  selectedClaimsType = 'simple';

  hideSection = false;

  hiddenFields: string[] = [];

  year = new Date().getFullYear();

  subs: Subscription[] = [];

  constructor(
    private changeDetector: ChangeDetectorRef,
    private conditionEvaluatorService: ConditionEvaluatorService
  ) {}

  ngOnChanges() {
    // this.subs.push(this.form.get('past_due_rent_history').valueChanges.subscribe(() => this.recalculateTotal()));
    this.subs.push(this.form.get('past_due_rent').valueChanges.subscribe(() => this.recalculateTotal()));
    this.subs.push(this.form.get('late_fees').valueChanges.subscribe(() => this.recalculateTotal()));
    this.subs.push(this.form.get('damages').valueChanges.subscribe(() => this.recalculateTotal()));
    this.subs.push(this.form.get('costs').valueChanges.subscribe(() => this.recalculateTotal()));
    this.subs.push(this.form.get('civil_recovery_fees').valueChanges.subscribe(() => this.recalculateTotal()));
    this.subs.push(this.form.get('attorney_fee').valueChanges.subscribe(() => this.recalculateTotal()));
    this.subs.push(this.form.get('partial_payments').valueChanges.subscribe(() => this.recalculateTotal()));
    if (this.claimsFields) {
      this.subs.push(this.claimsFields.valueChanges.subscribe(() => this.recalculateTotal()));
    }

    if (
      (this.options && this.options[this.intakeFields.CLAIMS].type === 'detailed') ||
      this.form.controls.past_due_rent.value ||
      this.form.controls.late_fees.value ||
      this.form.controls.damages.value ||
      this.form.controls.costs.value ||
      this.form.controls.civil_recovery_fees.value ||
      this.form.controls.attorney_fee.value ||
      this.form.controls.partial_payments.value
    ) {
      this.selectedClaimsType = 'detailed';
    }

    this.hideSection = false;
    this.form.enable();

    const tenantTotalOwedControl = this.form.get('tenant_total_owed');

    if (this.claimsSection?.rules?.length) {
      const hiddenFields: string[] = [];
      let hasOptionalRule = false;

      this.claimsSection.rules.forEach((rule) => {
        // calculate the rule
        if (
          this.conditionEvaluatorService.evaluateRuleDefinition(
            rule.source_field,
            rule.operator,
            rule.value,
            this.eviction
          )
        ) {
          if (rule.target_type === RuleTargetType.SECTION) {
            if (rule.action === RuleTargetAction.DISABLE || rule.action === RuleTargetAction.HIDE) {
              this.form.disable();
              this.form.reset();
            }
            if (rule.action === RuleTargetAction.HIDE) {
              this.hideSection = true;
            }
            if (rule.action === RuleTargetAction.OPTIONAL) {
              hasOptionalRule = true;
              // Remove required validator from tenant_total_owed (keep min and pattern validators)
              if (tenantTotalOwedControl) {
                tenantTotalOwedControl.clearValidators();
                tenantTotalOwedControl.updateValueAndValidity({ emitEvent: false });
              }

              // Remove required validators from all custom fields
              if (this.claimsFields && this.claimsSection?.fields) {
                this.claimsSection.fields.forEach((field) => {
                  const fieldControl = this.claimsFields.get(field.field);
                  if (fieldControl) {
                    // Clear all validators - custom fields typically only have required
                    fieldControl.clearValidators();
                    fieldControl.updateValueAndValidity({ emitEvent: false });
                  }
                });
              }
            }
          } else if (rule.target_type === RuleTargetType.FIELD) {
            const field = this.form?.get(rule.target_field?.field);
            if (field) {
              if (rule.action === RuleTargetAction.DISABLE || rule.action === RuleTargetAction.HIDE) {
                field.disable();
                field.reset();
              }
              if (rule.action === RuleTargetAction.HIDE) {
                hiddenFields.push(rule.target_field?.field);
              }
            }
          }
        } else {
          this.form.updateValueAndValidity();
        }
      });

      // If no OPTIONAL rule is active, restore validators
      if (!hasOptionalRule) {
        // Restore required validator for tenant_total_owed
        if (tenantTotalOwedControl) {
          tenantTotalOwedControl.setValidators([
            Validators.required,
            Validators.min(0.01),
            Validators.pattern(CURRENCY)
          ]);
          tenantTotalOwedControl.updateValueAndValidity({ emitEvent: false });
        }

        // Restore required validators for custom fields that should be required
        if (this.claimsFields && this.claimsSection?.fields) {
          this.claimsSection.fields.forEach((field) => {
            const fieldControl = this.claimsFields.get(field.field);
            if (fieldControl && field.required && !field.hide_from_intake) {
              fieldControl.setValidators([Validators.required]);
              fieldControl.updateValueAndValidity({ emitEvent: false });
            }
          });
        }
      }

      this.hiddenFields = hiddenFields;
    } else {
      // No rules, ensure tenant_total_owed has required validator
      if (tenantTotalOwedControl) {
        tenantTotalOwedControl.setValidators([Validators.required, Validators.min(0.01), Validators.pattern(CURRENCY)]);
        tenantTotalOwedControl.updateValueAndValidity({ emitEvent: false });
      }
    }
  }

  showField(field: IntakeFormFields, option: IntakeOption): boolean {
    return (
      option?.show &&
      (!option.hide_from_intake || this.demoMode || this.overrideFieldVisibility) &&
      !this.hiddenFields.includes(field)
    );
  }

  showingAtLeastOne(...fields: IntakeFormFields[]): boolean {
    let show = false;
    fields.forEach((f) => {
      show = show || this.showField(f, this.options[f]);
    });
    return show;
  }

  ngAfterViewChecked() {
    this.changeDetector.detectChanges();
  }

  ngOnDestroy(): void {
    this.subs.forEach((x) => x.unsubscribe());
  }

  get claimsFields(): UntypedFormGroup | undefined {
    return this.form.get('custom_fields') as UntypedFormGroup;
  }

  get getTotalOutstandingAmount() {
    return this.form.get('tenant_total_owed').value ?? 0;
  }

  updateTotal(value: number) {
    this.form.controls.past_due_rent.setValue(value);
  }

  private recalculateTotal() {
    let total = new Big(0);
    if (this.form.controls.past_due_rent.value) {
      total = total.add(this.form.controls.past_due_rent.value);
    }
    if (this.form.controls.late_fees.value) {
      total = total.add(this.form.controls.late_fees.value);
    }
    if (this.form.controls.damages.value) {
      total = total.add(this.form.controls.damages.value);
    }
    if (this.form.controls.costs.value) {
      total = total.add(this.form.controls.costs.value);
    }
    if (this.form.controls.civil_recovery_fees.value) {
      total = total.add(this.form.controls.civil_recovery_fees.value);
    }
    if (this.form.controls.attorney_fee.value) {
      total = total.add(this.form.controls.attorney_fee.value);
    }
    if (this.form.controls.partial_payments.value) {
      total = total.sub(this.form.controls.partial_payments.value);
    }

    if (this.claimsSection?.fields?.length) {
      this.claimsSection.fields.forEach((f) => {
        if (f.include_in_total && this.claimsFields && this.claimsFields.contains(f.field)) {
          const fieldControl = this.claimsFields.get(f.field);
          if (fieldControl && fieldControl.value) {
            total = total.add(fieldControl.value);
          }
        }
      });
    }

    if (this.options && this.options[this.intakeFields.CLAIMS]?.type === 'simple') {
      total = total.add(this.form.controls.tenant_total_owed.value ?? 0);
    }

    this.form.get('tenant_total_owed').setValue(total.toNumber());
  }
}
